import "@/App.css";
import { BrowserRouter as Router, Routes, Route, Link } from "react-router-dom";
import { Phone, MapPin, Award, GraduationCap, Stethoscope, Heart, Star, Menu, X, ChevronRight, Mail, Clock, Linkedin, ExternalLink } from "lucide-react";
import { useState, useEffect } from "react";
import { SpecializationPage, TreatmentPage } from "./pages/DetailPages";
import { specializationsData, treatmentsData } from "./data/specializations";

// Doctor Data
const doctorData = {
  name: "Dr. Prathyush V",
  title: "Consultant Medical and Hemato Oncologist",
  phone: "+91 9901305665",
  email: "contact@cancertherapyindia.com",
  experience: "12+",
  rating: "5.0",
  reviews: "36",
  photo: "https://drupal-cdn-hfaeddcdbng5hfbg.a01.azurefd.net/sites/default/files/2025-07/screenshot-2025-07-03-at-4.38.17-pm.png",
  linkedin: "https://www.linkedin.com/in/dr-prathyush-v-8ab988117",
  googleProfile: "https://www.google.com/search?kgmid=%2Fg%2F11vx1vzk3d",
  bio: `Dr. Prathyush V is a distinguished Medical Oncologist with over 12 years of dedicated experience in the field of oncology. Based in Bangalore, he is currently associated with Apollo Cancer Centres, where he has made significant contributions to cancer care and treatment.

Dr. Prathyush's robust academic foundation equips him with a comprehensive understanding of cancer treatment modalities, enabling him to provide the highest standard of care to his patients. Throughout his career, Dr. Prathyush has focused on a patient-centered approach, ensuring that each individual receives personalized treatment tailored to their unique needs.

His clinical expertise encompasses a wide range of oncological conditions, and he is particularly skilled in the latest advancements in cancer therapies, including targeted treatments and immunotherapy. He takes the time to explain complex medical information in an accessible manner, ensuring that patients and their families are well-informed and comfortable throughout their journey.`,
  qualifications: [
    "MBBS",
    "MD Haematology",
    "DrNB Medical Oncology (NBE)",
  ],
  expertise: [
    "Chemotherapy",
    "Immunotherapy",
    "Targeted Therapy",
    "Bone Marrow Transplant",
    "Precision Oncology",
    "Adult & Pediatric Cancer Care",
  ],
};

const specializations = {
  solidTumors: [
    { name: "Breast Cancer", slug: "breast-cancer", description: "Comprehensive treatment including surgery coordination, chemotherapy, hormonal therapy, and targeted treatments." },
    { name: "Lung Cancer", slug: "lung-cancer", description: "Advanced therapies including immunotherapy and targeted treatments for all stages of lung cancer." },
    { name: "Head & Neck Cancer", slug: "head-neck-cancer", description: "Multidisciplinary approach combining medical oncology with surgical and radiation therapy." },
    { name: "Oral Cancer", slug: "oral-cancer", description: "Specialized care for oral malignancies with focus on quality of life preservation." },
  ],
  bloodCancers: [
    { name: "Leukemia", slug: "leukemia", description: "Treatment of acute and chronic leukemias with latest protocols and supportive care." },
    { name: "Lymphoma", slug: "lymphoma", description: "Hodgkin and Non-Hodgkin lymphoma management with immunotherapy options." },
    { name: "Multiple Myeloma", slug: "multiple-myeloma", description: "Advanced treatment strategies including novel agents and transplant options." },
  ],
};

const treatments = [
  { name: "Chemotherapy", slug: "chemotherapy", image: "https://www.cancertherapyindia.com/wp-content/uploads/2025/12/chemotherapy.png" },
  { name: "Immunotherapy", slug: "immunotherapy", image: "https://www.cancertherapyindia.com/wp-content/uploads/2025/12/immuno-herapy.png" },
  { name: "Radiation Therapy", slug: "radiation-therapy", image: "https://www.cancertherapyindia.com/wp-content/uploads/2025/12/radiotherapy.png" },
  { name: "Bone Marrow Transplant", slug: "bone-marrow-transplant", image: "https://www.cancertherapyindia.com/wp-content/uploads/2025/12/bone.png" },
  { name: "Targeted Therapy", slug: "targeted-therapy", image: "https://www.cancertherapyindia.com/wp-content/uploads/2025/12/targeted-therapy.png" },
];

const locations = [
  {
    name: "Apollo Hospital Bannerghatta",
    address: "154, IIM, 11, Bannerghatta Main Rd, Krishnaraju Layout, Amalodbhavi Nagar, Bengaluru, Karnataka 560076",
    mapLink: "https://www.google.com/maps/place/Apollo+Hospitals+Bannerghatta/@12.8961106,77.5963533,17z",
    image: "https://www.cancertherapyindia.com/wp-content/uploads/2025/12/appolo-bhannergatta.png",
  },
  {
    name: "Apollo Hospital Jayanagar",
    address: "14th Cross Road, 212, Dr Parvathamma Rajkumar Rd, near Madhavan Park Circle, Jayanagar 3rd Block, Bengaluru 560011",
    mapLink: "https://www.google.com/maps/place/Apollo+Speciality+Hospital",
    image: "https://www.cancertherapyindia.com/wp-content/uploads/2025/12/cancer-center-electronic-city.jpg",
  },
  {
    name: "Apollo Hospital Seshadripuram",
    address: "Old No. 28, 1, Platform Rd, near Mantri Square Mall, VV Giri Colony, Bengaluru, Karnataka 560020",
    mapLink: "https://www.google.com/maps/place/Apollo+Hospital+Seshadripuram/@12.9883427,77.5724257,17z",
    image: "https://www.cancertherapyindia.com/wp-content/uploads/2025/12/appolo-sheesh.png",
  },
];

const testimonials = [
  {
    name: "Vamsi",
    condition: "Leukaemia Treatment",
    quote: "Dr. Prathyush V provided exceptional care throughout my leukaemia treatment. His expertise and compassionate approach made all the difference in my recovery journey.",
    rating: 5,
    source: "Google Review",
  },
  {
    name: "Manasa Yadav",
    condition: "Cancer Care",
    quote: "Dr Prathyush is a good and highly recommend doctor. His attention to detail and patient care is outstanding. Truly grateful for his guidance.",
    rating: 5,
    source: "Google Review",
  },
  {
    name: "Shravan Jannu",
    condition: "Family Treatment",
    quote: "We family are grateful to him. Dr. Prathyush treated our loved one with utmost care and professionalism. The entire team was supportive throughout.",
    rating: 5,
    source: "Google Review",
  },
];

// Header Component
const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navItems = [
    { label: "About", href: "#about" },
    { label: "Specializations", href: "#specializations" },
    { label: "Treatments", href: "#treatments" },
    { label: "Locations", href: "#locations" },
    { label: "Testimonials", href: "#testimonials" },
  ];

  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "glass shadow-sm" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <Link to="/" data-testid="logo" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#2C5545] flex items-center justify-center">
              <Stethoscope className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="font-semibold text-[#1A1D1A] text-sm sm:text-base font-['Outfit']">Dr. Prathyush V</p>
              <p className="text-xs text-[#5C6B64] hidden sm:block">Medical Oncologist</p>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
            {navItems.map((item) => (
              <a
                key={item.label}
                href={item.href}
                className="text-[#5C6B64] hover:text-[#2C5545] transition-colors text-sm font-medium"
              >
                {item.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-4">
            <a
              href={`tel:${doctorData.phone}`}
              data-testid="header-cta"
              className="hidden sm:flex items-center gap-2 bg-[#2C5545] hover:bg-[#224235] text-white px-5 py-2.5 rounded-full transition-all duration-300 hover:scale-105 text-sm font-medium"
            >
              <Phone className="w-4 h-4" />
              <span>Book Consultation</span>
            </a>

            <button
              data-testid="mobile-menu-btn"
              className="md:hidden p-2 text-[#1A1D1A]"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {isMobileMenuOpen && (
          <nav data-testid="mobile-nav" className="md:hidden mt-4 pb-4 border-t border-[#E5E7E2] pt-4">
            <div className="flex flex-col gap-4">
              {navItems.map((item) => (
                <a
                  key={item.label}
                  href={item.href}
                  className="text-[#5C6B64] hover:text-[#2C5545] transition-colors text-sm font-medium"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {item.label}
                </a>
              ))}
              <a
                href={`tel:${doctorData.phone}`}
                className="flex items-center gap-2 bg-[#2C5545] hover:bg-[#224235] text-white px-5 py-2.5 rounded-full transition-all duration-300 text-sm font-medium w-fit"
              >
                <Phone className="w-4 h-4" />
                <span>Book Consultation</span>
              </a>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

// Hero Section
const Hero = () => {
  return (
    <section data-testid="hero-section" className="relative min-h-screen flex items-center pt-20 overflow-hidden">
      <div className="absolute inset-0 hero-pattern opacity-30" />
      <div className="absolute inset-0 bg-gradient-to-br from-[#F9F8F6] via-[#F9F8F6]/95 to-[#F9F8F6]/80" />

      <div className="relative max-w-7xl mx-auto px-6 py-16 lg:py-24">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="order-2 lg:order-1 animate-fade-in-up">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#D4A373] mb-4">
              Expert Oncologist | Adult & Pediatric Cancer Care
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight font-light text-[#1A1D1A] font-['Outfit'] mb-6">
              Dr. Prathyush V
            </h1>
            <p className="text-base leading-relaxed text-[#5C6B64] mb-8 max-w-xl">
              Delivering compassionate, evidence-based cancer care at Apollo Hospitals, Bangalore. Specializing in solid tumors and blood cancers with a patient-first approach.
            </p>

            <div className="flex flex-wrap gap-6 mb-10">
              <div className="flex items-center gap-2 bg-white/80 px-4 py-2 rounded-full border border-[#E5E7E2]">
                <Award className="w-5 h-5 text-[#D4A373]" />
                <span className="text-sm text-[#5C6B64] font-medium">{doctorData.experience} Years Experience</span>
              </div>
              <div className="flex items-center gap-2 bg-white/80 px-4 py-2 rounded-full border border-[#E5E7E2]">
                <Star className="w-5 h-5 fill-[#D4A373] text-[#D4A373]" />
                <span className="text-sm text-[#5C6B64] font-medium">{doctorData.rating} ({doctorData.reviews} Reviews)</span>
              </div>
            </div>

            <div className="flex flex-wrap gap-4">
              <a
                href={`tel:${doctorData.phone}`}
                data-testid="hero-cta-primary"
                className="flex items-center gap-2 bg-[#2C5545] hover:bg-[#224235] text-white px-8 py-4 rounded-full transition-all duration-300 hover:scale-105 font-medium"
              >
                <Phone className="w-5 h-5" />
                <span>Call Now: {doctorData.phone}</span>
              </a>
              <a
                href="#about"
                data-testid="hero-cta-secondary"
                className="flex items-center gap-2 border-2 border-[#2C5545] text-[#2C5545] hover:bg-[#2C5545] hover:text-white px-8 py-4 rounded-full transition-all duration-300 font-medium"
              >
                <span>Learn More</span>
                <ChevronRight className="w-5 h-5" />
              </a>
            </div>
          </div>

          <div className="order-1 lg:order-2 animate-fade-in stagger-2">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-br from-[#2C5545]/10 to-[#D4A373]/10 rounded-3xl blur-2xl" />
              <div className="relative bg-white rounded-3xl overflow-hidden card-shadow border border-[#E5E7E2]">
                <img
                  src={doctorData.photo}
                  alt="Dr. Prathyush V - Medical Oncologist"
                  className="w-full h-[400px] sm:h-[500px] object-cover object-top"
                  data-testid="hero-image"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#1A1D1A]/80 to-transparent p-6">
                  <p className="text-white font-['Outfit'] font-medium">Apollo Hospitals</p>
                  <p className="text-white/80 text-sm">Bangalore, Karnataka</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// About Section
const About = () => {
  return (
    <section id="about" data-testid="about-section" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div className="animate-slide-in-left">
            <div className="relative">
              <div className="absolute -inset-4 bg-[#2C5545]/5 rounded-3xl" />
              <img
                src={doctorData.photo}
                alt="Dr. Prathyush V - Medical Oncologist"
                className="relative rounded-3xl w-full h-[400px] lg:h-[500px] object-cover object-top card-shadow"
                data-testid="about-image"
              />
              <div className="absolute bottom-4 left-4 right-4 flex gap-3">
                <a
                  href={doctorData.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 bg-white/90 backdrop-blur-sm px-4 py-2 rounded-full text-[#0077B5] hover:bg-[#0077B5] hover:text-white transition-all duration-300 text-sm font-medium"
                  data-testid="linkedin-link"
                >
                  <Linkedin className="w-4 h-4" />
                  <span>LinkedIn</span>
                </a>
                <a
                  href={doctorData.googleProfile}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 bg-white/90 backdrop-blur-sm px-4 py-2 rounded-full text-[#4285F4] hover:bg-[#4285F4] hover:text-white transition-all duration-300 text-sm font-medium"
                  data-testid="google-profile-link"
                >
                  <ExternalLink className="w-4 h-4" />
                  <span>Google</span>
                </a>
              </div>
            </div>
          </div>

          <div className="animate-slide-in-right">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#D4A373] mb-4">About</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium text-[#1A1D1A] font-['Outfit'] mb-6">
              Compassionate Care, Advanced Treatment
            </h2>
            <p className="text-base leading-relaxed text-[#5C6B64] mb-8 whitespace-pre-line">
              {doctorData.bio}
            </p>

            <div className="mb-8">
              <h3 className="flex items-center gap-2 text-xl font-medium text-[#2C5545] font-['Outfit'] mb-4">
                <GraduationCap className="w-5 h-5" />
                Qualifications
              </h3>
              <ul className="grid gap-2">
                {doctorData.qualifications.map((qual, idx) => (
                  <li key={idx} className="flex items-center gap-3 text-[#5C6B64]">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#D4A373]" />
                    {qual}
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="flex items-center gap-2 text-xl font-medium text-[#2C5545] font-['Outfit'] mb-4">
                <Award className="w-5 h-5" />
                Areas of Expertise
              </h3>
              <div className="flex flex-wrap gap-2">
                {doctorData.expertise.map((exp, idx) => (
                  <span
                    key={idx}
                    className="px-4 py-2 bg-[#F9F8F6] text-[#5C6B64] rounded-full text-sm border border-[#E5E7E2]"
                  >
                    {exp}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Specializations Section
const Specializations = () => {
  return (
    <section id="specializations" data-testid="specializations-section" className="py-24 bg-[#F9F8F6] relative overflow-hidden">
      <div 
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: `url("https://static.prod-images.emergentagent.com/jobs/146e6600-24a9-4669-850c-83248fde8d02/images/4f93ff314bbc8d7ac192c51f2139745a4809c75cf4f163c48dc6f90efe9c9d77.png")`,
          backgroundSize: "cover",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6">
        <div className="text-center mb-16 animate-fade-in-up">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#D4A373] mb-4">Specializations</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium text-[#1A1D1A] font-['Outfit'] mb-4">
            Comprehensive Cancer Care
          </h2>
          <p className="text-base text-[#5C6B64] max-w-2xl mx-auto">
            Expert treatment for all types of solid tumors and blood cancers. Click on any specialization to learn more.
          </p>
        </div>

        {/* Solid Tumors */}
        <div className="mb-16">
          <h3 className="text-xl sm:text-2xl font-medium text-[#2C5545] font-['Outfit'] mb-8 flex items-center gap-3">
            <span className="w-8 h-8 rounded-full bg-[#2C5545] flex items-center justify-center">
              <Stethoscope className="w-4 h-4 text-white" />
            </span>
            Solid Tumors
          </h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {specializations.solidTumors.map((spec, idx) => (
              <Link
                key={idx}
                to={`/specialization/${spec.slug}`}
                data-testid={`solid-tumor-${idx}`}
                className="bg-white p-6 rounded-2xl card-shadow border border-[#E5E7E2] hover-lift group cursor-pointer"
              >
                <h4 className="text-lg font-medium text-[#1A1D1A] font-['Outfit'] mb-2 group-hover:text-[#2C5545] transition-colors">{spec.name}</h4>
                <p className="text-sm text-[#5C6B64] leading-relaxed mb-3">{spec.description}</p>
                <span className="text-sm text-[#D4A373] font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                  Learn More <ChevronRight className="w-4 h-4" />
                </span>
              </Link>
            ))}
          </div>
        </div>

        {/* Blood Cancers */}
        <div>
          <h3 className="text-xl sm:text-2xl font-medium text-[#2C5545] font-['Outfit'] mb-8 flex items-center gap-3">
            <span className="w-8 h-8 rounded-full bg-[#D4A373] flex items-center justify-center">
              <Heart className="w-4 h-4 text-white" />
            </span>
            Blood Cancers & Hematological Disorders
          </h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {specializations.bloodCancers.map((spec, idx) => (
              <Link
                key={idx}
                to={`/specialization/${spec.slug}`}
                data-testid={`blood-cancer-${idx}`}
                className="bg-white p-6 rounded-2xl card-shadow border border-[#E5E7E2] hover-lift group cursor-pointer"
              >
                <h4 className="text-lg font-medium text-[#1A1D1A] font-['Outfit'] mb-2 group-hover:text-[#2C5545] transition-colors">{spec.name}</h4>
                <p className="text-sm text-[#5C6B64] leading-relaxed mb-3">{spec.description}</p>
                <span className="text-sm text-[#D4A373] font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                  Learn More <ChevronRight className="w-4 h-4" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Treatments Section
const Treatments = () => {
  return (
    <section id="treatments" data-testid="treatments-section" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#D4A373] mb-4">Treatments</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium text-[#1A1D1A] font-['Outfit'] mb-4">
            Treatment & Procedures
          </h2>
          <p className="text-base text-[#5C6B64] max-w-2xl mx-auto">
            Advanced cancer treatment modalities tailored to your specific needs.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
          {treatments.map((treatment, idx) => (
            <Link
              key={idx}
              to={`/treatment/${treatment.slug}`}
              data-testid={`treatment-${idx}`}
              className="bg-[#F9F8F6] rounded-2xl overflow-hidden card-shadow border border-[#E5E7E2] hover-lift group"
            >
              <div className="h-32 overflow-hidden bg-[#2C5545]/5 flex items-center justify-center">
                <img
                  src={treatment.image}
                  alt={treatment.name}
                  className="w-20 h-20 object-contain"
                />
              </div>
              <div className="p-4 text-center">
                <h3 className="font-medium text-[#1A1D1A] font-['Outfit'] group-hover:text-[#2C5545] transition-colors">{treatment.name}</h3>
                <span className="text-xs text-[#D4A373] mt-2 inline-flex items-center gap-1">
                  Read More <ChevronRight className="w-3 h-3" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

// Locations Section
const Locations = () => {
  return (
    <section id="locations" data-testid="locations-section" className="py-24 bg-[#F9F8F6]">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16 animate-fade-in-up">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#D4A373] mb-4">Locations</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium text-[#1A1D1A] font-['Outfit'] mb-4">
            Apollo Hospitals, Bangalore
          </h2>
          <p className="text-base text-[#5C6B64] max-w-2xl mx-auto">
            Consult Dr. Prathyush V at any of the three convenient Apollo Hospital locations across Bangalore.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {locations.map((location, idx) => (
            <div
              key={idx}
              data-testid={`location-${idx}`}
              className="bg-white rounded-2xl overflow-hidden card-shadow border border-[#E5E7E2] hover-lift"
            >
              <div className="h-48 overflow-hidden">
                <img
                  src={location.image}
                  alt={location.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="text-lg font-medium text-[#1A1D1A] font-['Outfit'] mb-3">{location.name}</h3>
                <p className="text-sm text-[#5C6B64] mb-4 leading-relaxed">{location.address}</p>
                <div className="flex flex-col gap-3">
                  <a
                    href={`tel:${doctorData.phone}`}
                    className="flex items-center gap-2 text-[#2C5545] hover:text-[#224235] font-medium text-sm"
                  >
                    <Phone className="w-4 h-4" />
                    {doctorData.phone}
                  </a>
                  <a
                    href={location.mapLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-[#D4A373] hover:text-[#c4935f] font-medium text-sm"
                  >
                    <MapPin className="w-4 h-4" />
                    Get Directions
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Testimonials Section
const Testimonials = () => {
  return (
    <section id="testimonials" data-testid="testimonials-section" className="py-24 relative overflow-hidden bg-white">
      <div className="relative max-w-7xl mx-auto px-6">
        <div className="text-center mb-16 animate-fade-in-up">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#D4A373] mb-4">Testimonials</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium text-[#1A1D1A] font-['Outfit'] mb-4">
            What Patients Say
          </h2>
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-5 h-5 fill-[#D4A373] text-[#D4A373]" />
              ))}
            </div>
            <span className="text-[#5C6B64] font-medium">{doctorData.rating} rating from {doctorData.reviews} Google Reviews</span>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, idx) => (
            <div
              key={idx}
              data-testid={`testimonial-${idx}`}
              className="bg-[#F9F8F6] p-8 rounded-2xl card-shadow border border-[#E5E7E2] hover-lift"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#2C5545] to-[#D4A373] flex items-center justify-center text-white text-xl font-semibold font-['Outfit']">
                  {testimonial.name.charAt(0)}
                </div>
                <div>
                  <h4 className="font-medium text-[#1A1D1A] font-['Outfit']">{testimonial.name}</h4>
                  <p className="text-sm text-[#D4A373]">{testimonial.condition}</p>
                </div>
              </div>
              <div className="flex gap-1 mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-[#D4A373] text-[#D4A373]" />
                ))}
              </div>
              <p className="text-[#5C6B64] text-sm leading-relaxed mb-4">"{testimonial.quote}"</p>
              <p className="text-xs text-[#5C6B64]/60 flex items-center gap-1">
                <span className="inline-block w-4 h-4 bg-[#4285F4] rounded-full flex items-center justify-center text-white text-[8px] font-bold">G</span>
                {testimonial.source}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// CTA Section
const CTA = () => {
  return (
    <section data-testid="cta-section" className="py-24 bg-[#2C5545]">
      <div className="max-w-4xl mx-auto px-6 text-center">
        <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium text-white font-['Outfit'] mb-6">
          Take the First Step Towards Recovery
        </h2>
        <p className="text-white/80 mb-10 max-w-2xl mx-auto">
          Schedule a consultation with Dr. Prathyush V today. Our team is ready to provide you with compassionate, world-class cancer care.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
          <a
            href={`tel:${doctorData.phone}`}
            data-testid="cta-phone"
            className="flex items-center gap-3 bg-white text-[#2C5545] hover:bg-[#F9F8F6] px-8 py-4 rounded-full transition-all duration-300 hover:scale-105 font-medium"
          >
            <Phone className="w-5 h-5" />
            <span>{doctorData.phone}</span>
          </a>
          <div className="flex items-center gap-3 text-white/80">
            <Clock className="w-5 h-5" />
            <span>Mon - Sat: 9:00 AM - 6:00 PM</span>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer data-testid="footer" className="bg-[#1A1D1A] text-white py-16">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid md:grid-cols-4 gap-12 mb-12">
          <div className="md:col-span-2">
            <Link to="/" className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-[#2C5545] flex items-center justify-center">
                <Stethoscope className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-semibold text-white font-['Outfit']">Dr. Prathyush V</p>
                <p className="text-sm text-white/60">Medical & Hemato Oncologist</p>
              </div>
            </Link>
            <p className="text-white/60 text-sm leading-relaxed max-w-md mb-4">
              Providing compassionate, evidence-based cancer care at Apollo Hospitals, Bangalore. Specializing in solid tumors and blood cancers with a patient-first approach.
            </p>
            <div className="flex gap-3">
              <a
                href={doctorData.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-[#0077B5] flex items-center justify-center transition-all duration-300"
                data-testid="footer-linkedin"
              >
                <Linkedin className="w-5 h-5" />
              </a>
            </div>
          </div>

          <div>
            <h4 className="font-medium font-['Outfit'] mb-4">Quick Links</h4>
            <ul className="space-y-2 text-sm text-white/60">
              <li><a href="#about" className="hover:text-white transition-colors">About</a></li>
              <li><a href="#specializations" className="hover:text-white transition-colors">Specializations</a></li>
              <li><a href="#treatments" className="hover:text-white transition-colors">Treatments</a></li>
              <li><a href="#locations" className="hover:text-white transition-colors">Locations</a></li>
              <li><a href="#testimonials" className="hover:text-white transition-colors">Testimonials</a></li>
            </ul>
          </div>

          <div>
            <h4 className="font-medium font-['Outfit'] mb-4">Contact</h4>
            <ul className="space-y-3 text-sm text-white/60">
              <li>
                <a href={`tel:${doctorData.phone}`} className="flex items-center gap-2 hover:text-white transition-colors">
                  <Phone className="w-4 h-4" />
                  {doctorData.phone}
                </a>
              </li>
              <li>
                <a href={`mailto:${doctorData.email}`} className="flex items-center gap-2 hover:text-white transition-colors">
                  <Mail className="w-4 h-4" />
                  {doctorData.email}
                </a>
              </li>
              <li>
                <a href={doctorData.linkedin} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-white transition-colors">
                  <Linkedin className="w-4 h-4" />
                  LinkedIn Profile
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-white/40 text-sm">
            © {new Date().getFullYear()} Dr. Prathyush V - Apollo Hospitals, Bangalore. All rights reserved.
          </p>
          <p className="text-white/40 text-sm">
            Apollo Hospitals, Bangalore
          </p>
        </div>
      </div>
    </footer>
  );
};

// Home Page Component
const HomePage = () => {
  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <Header />
      <main>
        <Hero />
        <About />
        <Specializations />
        <Treatments />
        <Locations />
        <Testimonials />
        <CTA />
      </main>
      <Footer />
    </div>
  );
};

// Main App Component with Router
function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/specialization/:slug" element={<SpecializationPage />} />
        <Route path="/treatment/:slug" element={<TreatmentPage />} />
      </Routes>
    </Router>
  );
}

export default App;
